import { Card } from '@cmsgov/ds-medicare-gov'
import classNames from 'classnames'
import { MyLink } from 'components/MyLink'
import { useEvent } from 'hooks/useEvent'
import { useMedicareUrl } from 'hooks/useMedicareUrl'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import Skeleton from 'react-loading-skeleton'
import { DeductibleErrorAlert } from '../DeductibleErrorAlert/DeductibleErrorAlert'

import classes from './DeductibleStatusCard.module.scss'

type DeductibleStatusCardProps = {
  deductible: number
  amountPaid: number
  isLoading?: boolean
  isError?: boolean
}
export const DeductibleStatusCard = ({
  amountPaid,
  deductible,
  isLoading,
  isError
}: DeductibleStatusCardProps) => {
  const { t } = useTranslation('accountHome', {
    keyPrefix: 'currentDeductibles'
  })
  const { fireEvent } = useEvent('accountHome')

  const pct = Math.round((amountPaid / deductible) * 100)

  const currentYear = new Date().getFullYear()

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  })
  const formattedAmount = formatter.format(amountPaid)
  const formattedRemaining = formatter.format(deductible - amountPaid)
  const formattedDeductible = formatter.format(deductible)

  const deductibleHistoryLink = useMedicareUrl(
    'https://www.medicare.gov/my/claims/claim-deductibles',
    'https://es.medicare.gov/my/claims/claim-deductibles '
  )

  const handleLinkClick = useCallback(
    ({ linkText, linkUrl }) => {
      fireEvent('accounthome_openmydeductibles_click', { linkText, linkUrl })
    },
    [fireEvent]
  )

  if (isError) {
    return (
      <Card className={classNames('ds-u-padding--3', classes.statusCard)}>
        <h3 className="ds-text-heading--lg ds-u-margin-bottom--3">
          {t('currentStatusHeading')}
        </h3>
        <DeductibleErrorAlert />
      </Card>
    )
  }

  return (
    <Card className={`ds-u-display--inline-block ${classes.card}`}>
      <div className={classes.grid}>
        <div className={`ds-u-padding--3 ${classes.currentDeductible}`}>
          <h3 className="ds-text-heading--lg ds-u-margin-bottom--3">
            {isLoading ? <Skeleton /> : t('currentStatusHeading')}
          </h3>
          {isLoading
            ? (
            <div className="ds-text-heading--lg">
              <Skeleton />
            </div>
              )
            : (
            <div className={classes.progressOuter}>
              <div
                style={{ width: `${pct}%` }}
                className={classes.progressInner}
              ></div>
            </div>
              )}

          {!isError && (
            <div className="ds-u-display--flex ds-u-justify-content--between ds-u-margin-top--2">
              <div>
                <div className="ds-text-body--md ds-u-margin--0">
                  {isLoading ? <Skeleton /> : t('amountPaid')}
                </div>
                <div className="ds-text-body--lg ds-u-font-weight--semibold">
                  {isLoading ? <Skeleton /> : formattedAmount}
                </div>
              </div>
              <div>
                <div className="ds-text-body--md ds-u-margin--0">
                  {isLoading ? <Skeleton /> : t('amountRemaining')}
                </div>
                <div className="ds-text-body--lg">
                  {isLoading ? <Skeleton /> : formattedRemaining}
                </div>
              </div>
            </div>
          )}
        </div>
        <hr className={classes.divider} />
        <div className={`ds-u-padding--3 ${classes.totalDeductible}`}>
          <h3 className="ds-text-heading--lg ds-u-margin-bottom--3">
            {isLoading
              ? (
              <Skeleton />
                )
              : (
                  t('totalHeading', { year: currentYear })
                )}
          </h3>
          <div
            className={`ds-text-body--lg ds-u-font-weight--semibold ${classes.totalValue}`}
          >
            {isLoading ? <Skeleton /> : formattedDeductible}
          </div>
          <div className={``}>
            {isLoading
              ? (
              <Skeleton />
                )
              : (
              <MyLink
                className="ds-c-link"
                href={deductibleHistoryLink}
                onClick={handleLinkClick}
              >
                {t('deductibleHistoryLink')}
              </MyLink>
                )}
          </div>
        </div>
      </div>
    </Card>
  )
}
